import React, { useState, useEffect, useCallback, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import Button from '../../components/ui/Button';
import GenericSelect from '../../components/ui/GenericSelect';
import CheckboxInput from '../../components/ui/CheckboxInput';
import { getShiftComplianceSettings, updateShiftComplianceSettings } from '../../services/shiftScheduleService';

// 7 kural alanı → backend rule code (ShiftComplianceRules.cs sabitleriyle
// birebir; i18n anahtarı `shiftCompliance.rule.<code>`, Task 15'te eklenir).
// Sıra backend'in Sev switch'i ve Task 15 plan dokümanındaki sırayla aynı.
const RULES = [
  { field: 'restPeriodSeverity', code: 'rest_11h' },
  { field: 'nightWorkSeverity', code: 'night_75' },
  { field: 'nightRotationSeverity', code: 'night_rotation_2w' },
  { field: 'weeklyRestSeverity', code: 'weekly_rest_24h' },
  { field: 'minorNightSeverity', code: 'minor_night' },
  { field: 'womenNightSeverity', code: 'women_night_75' },
  { field: 'weeklyHoursSeverity', code: 'weekly_45h_info' },
];

// Backend ValidSeverities sırasıyla aynı (ShiftComplianceDataAccess.cs).
const SEVERITY_VALUES = ['error', 'warning', 'info', 'off'];

// Kayıt hiç yoksa (tenant ayarları hiç kaydetmemiş) veya GET başarısız olursa
// gösterilecek — backend'in ShiftComplianceSettingsResponse varsayılanlarıyla
// birebir (ShiftComplianceDataAccess.ToResponse).
const DEFAULT_SETTINGS = {
  restPeriodSeverity: 'warning',
  nightWorkSeverity: 'warning',
  nightRotationSeverity: 'warning',
  weeklyRestSeverity: 'warning',
  minorNightSeverity: 'error',
  womenNightSeverity: 'warning',
  weeklyHoursSeverity: 'info',
  sectorNightExceptionEnabled: false,
};

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { transform: translateY(16px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

// ─── Styled Components (WorkHourPickerModal.js overlay/dialog deseni) ────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  animation: ${fadeIn} 0.15s ease;
`;

const Dialog = styled.div`
  background: #fff;
  border-radius: 8px;
  width: 480px;
  max-width: 92vw;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.18);
  animation: ${slideUp} 0.18s ease;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #E8ECF0;
`;

const TitleText = styled.h3`
  font-size: ${({ theme }) => theme.metrics.font.size.md};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 700;
  font-family: 'Nunito Sans', sans-serif;
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background: none;
  color: #A0A8B4;
  cursor: pointer;
  &:hover { background: #F5F7FA; }
`;

const Body = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const LoadingState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
  color: #7A8694;
  font-size: 14px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const RuleLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  flex: 1;
  min-width: 0;
`;

const SelectSlot = styled.div`
  width: 150px;
  flex-shrink: 0;
`;

const Divider = styled.div`
  border-top: 1px solid #E8ECF0;
  margin: 4px 0;
`;

const SectorBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Hint = styled.span`
  font-size: 12px;
  color: #7A8694;
  line-height: 1.5;
  padding-left: 30px; /* CheckboxInput: kutu 20px + gap 10px ile hizalı */
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #E8ECF0;
`;

export default function ComplianceSettingsModal({ isOpen, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Açılışta mevcut ayarları yükle. Kapalıyken hiçbir şey yapma; her açılışta
  // yeniden çeker (stale form'u önler). cancelled guard'ı ShiftSchedulesPage'in
  // workHours efekti ile aynı desen.
  useEffect(() => {
    if (!isOpen) return undefined;
    let cancelled = false;
    setLoading(true);
    getShiftComplianceSettings()
      .then((res) => {
        if (cancelled) return;
        const data = res.data?.data;
        if (data) {
          setForm({
            restPeriodSeverity: data.restPeriodSeverity || DEFAULT_SETTINGS.restPeriodSeverity,
            nightWorkSeverity: data.nightWorkSeverity || DEFAULT_SETTINGS.nightWorkSeverity,
            nightRotationSeverity: data.nightRotationSeverity || DEFAULT_SETTINGS.nightRotationSeverity,
            weeklyRestSeverity: data.weeklyRestSeverity || DEFAULT_SETTINGS.weeklyRestSeverity,
            minorNightSeverity: data.minorNightSeverity || DEFAULT_SETTINGS.minorNightSeverity,
            womenNightSeverity: data.womenNightSeverity || DEFAULT_SETTINGS.womenNightSeverity,
            weeklyHoursSeverity: data.weeklyHoursSeverity || DEFAULT_SETTINGS.weeklyHoursSeverity,
            sectorNightExceptionEnabled: !!data.sectorNightExceptionEnabled,
          });
        }
      })
      .catch(() => { if (!cancelled) setForm(DEFAULT_SETTINGS); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen]);

  const severityOptions = useMemo(
    () => SEVERITY_VALUES.map((value) => ({ id: value, name: t(`shiftCompliance.severity.${value}`) })),
    [t],
  );

  // GenericSelect'in "X" temizle ikonu onChange(null) tetikler — şiddet
  // alanları boş bırakılamaz (backend 400 döner), bu yüzden boş değeri yok say.
  const handleSeverityChange = useCallback((field) => (val) => {
    if (!val) return;
    setForm((f) => ({ ...f, [field]: val }));
  }, []);

  const handleRequestClose = useCallback(() => {
    if (saving) return;
    onClose?.();
  }, [saving, onClose]);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateShiftComplianceSettings(form);
      onSaved?.();
      onClose?.();
    } catch { /* interceptor */ }
    setSaving(false);
  }, [form, onSaved, onClose]);

  if (!isOpen) return null;

  return (
    <Overlay onClick={handleRequestClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        <Header>
          <TitleText>{t('shiftCompliance.settings.title')}</TitleText>
          <CloseBtn onClick={handleRequestClose} aria-label={t('common.close')}>
            <X size={18} />
          </CloseBtn>
        </Header>

        {loading ? (
          <LoadingState>{t('common.loading')}</LoadingState>
        ) : (
          <Body>
            {RULES.map((rule, idx) => (
              <Row key={rule.field}>
                <RuleLabel>{t(`shiftCompliance.rule.${rule.code}`)}</RuleLabel>
                <SelectSlot>
                  <GenericSelect
                    size="small"
                    options={severityOptions}
                    value={form[rule.field]}
                    onChange={handleSeverityChange(rule.field)}
                    direction={idx === RULES.length - 1 ? 'top' : 'bottom'}
                  />
                </SelectSlot>
              </Row>
            ))}

            <Divider />

            <SectorBlock>
              <CheckboxInput
                label={t('shiftCompliance.settings.sectorNightException')}
                checked={form.sectorNightExceptionEnabled}
                onChange={(val) => setForm((f) => ({ ...f, sectorNightExceptionEnabled: val }))}
              />
              <Hint>{t('shiftCompliance.settings.sectorNightExceptionHint')}</Hint>
            </SectorBlock>
          </Body>
        )}

        <Footer>
          <Button variant="ghost" onClick={handleRequestClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Footer>
      </Dialog>
    </Overlay>
  );
}
